import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCar, FaArrowLeft, FaPhone } from 'react-icons/fa';
import { resolveMediaUrl, vehicleAPI } from '../services/api';
import { toast } from 'react-toastify';
import './VehicleDetails.css';

const VehicleDetails = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const loadVehicle = useCallback(async () => {
    try {
      const response = await vehicleAPI.getById(id);
      if (response.data.success) {
        setVehicle(response.data.data);
        setSelectedImageIndex(0);
      } else {
        toast.error('Vehicle not found');
      }
    } catch (error) {
      toast.error('Error loading vehicle');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVehicle();
  }, [loadVehicle]);

  const formatPrice = (price) => {
    return `Rs. ${Number(price).toLocaleString('en-LK')}`;
  };

  useEffect(() => {
    if (vehicle) {
      document.title = `${vehicle.make} ${vehicle.model} (${vehicle.year}) | Kariyapperuma Car Sales`;
    }
    return () => { document.title = 'Kariyapperuma Car Sales'; };
  }, [vehicle]);

  const vehicleImages = vehicle?.images || [];
  const selectedImage = vehicleImages[selectedImageIndex] || vehicleImages[0] || null;
  const touchStartX = useRef(null);

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    if (vehicleImages.length <= 1) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') {
        setSelectedImageIndex(i => (i + 1) % vehicleImages.length);
      } else if (e.key === 'ArrowLeft') {
        setSelectedImageIndex(i => (i - 1 + vehicleImages.length) % vehicleImages.length);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [vehicleImages.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || vehicleImages.length <= 1) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) {
        setSelectedImageIndex(i => (i + 1) % vehicleImages.length);
      } else {
        setSelectedImageIndex(i => (i - 1 + vehicleImages.length) % vehicleImages.length);
      }
    }
    touchStartX.current = null;
  };

  const whatsappNumber = '94707352969';
  const inquiryMessage = encodeURIComponent(
    `Hi, I'm interested in ${vehicle?.make || ''} ${vehicle?.model || ''}`.trim()
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="not-found-container">
        <h2>Vehicle Not Found</h2>
        <Link to="/vehicles" className="btn btn-primary">Back to Vehicles</Link>
      </div>
    );
  }

  return (
    <div className="vehicle-details-page">
      <div className="container">
        <Link to="/vehicles" className="back-link">
          <FaArrowLeft /> Back to Vehicles
        </Link>

        <div className="vehicle-details-grid">
          <div className="vehicle-images">
            <div
              className="main-image"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {selectedImage ? (
                <img
                  src={resolveMediaUrl(selectedImage)}
                  alt={`${vehicle.make} ${vehicle.model}`}
                />
              ) : (
                <div className="placeholder-image">
                  <FaCar />
                  <p>Vehicle Image</p>
                </div>
              )}
              {vehicleImages.length > 1 && (
                <div className="image-counter">
                  {selectedImageIndex + 1} / {vehicleImages.length}
                </div>
              )}
            </div>

            {/* Image Thumbnails */}
            {vehicleImages.length > 1 && (
              <div className="image-thumbnails">
                {vehicleImages.map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={resolveMediaUrl(image)}
                      alt={`${vehicle.make} ${vehicle.model} thumbnail ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="vehicle-details-info">
            {vehicle.badge && <span className="vehicle-badge">{vehicle.badge}</span>}
            <h1>{vehicle.make} {vehicle.model}</h1>
            <div className="vehicle-price-large">{formatPrice(vehicle.price)}</div>

            <div className="vehicle-specs-grid">
              <div className="spec-item">
                <strong>Year</strong>
                <span>{vehicle.year}</span>
              </div>
              <div className="spec-item">
                <strong>Fuel Type</strong>
                <span>{vehicle.fuelType}</span>
              </div>
              <div className="spec-item">
                <strong>Transmission</strong>
                <span>{vehicle.transmission}</span>
              </div>
              <div className="spec-item">
                <strong>Engine</strong>
                <span>{vehicle.engineCapacity || 'On request'}</span>
              </div>
              <div className="spec-item">
                <strong>Mileage</strong>
                <span>{vehicle.mileage ? `${vehicle.mileage} km` : 'N/A'}</span>
              </div>
              <div className="spec-item">
                <strong>Color</strong>
                <span>{vehicle.color || 'On request'}</span>
              </div>
            </div>

            <div className="features-list">
              <h3>Features</h3>
              <ul>
                {vehicle.features && vehicle.features.length > 0 ? (
                  vehicle.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))
                ) : (
                  <li>No features listed</li>
                )}
              </ul>
            </div>

            <div className="description">
              <h3>Description</h3>
              <p>{vehicle.description || 'Contact us for full details about this vehicle.'}</p>
            </div>

            <div className="action-buttons">
              <a href="tel:+94707352969" className="btn btn-primary btn-lg">
                <FaPhone /> Call Now
              </a>
              <a 
                href={`https://wa.me/${whatsappNumber}?text=${inquiryMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-lg"
              >
                WhatsApp Inquiry
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetails;
